import { useEffect } from "react";
import { updateCollisionVisibility } from "../collisionVisualization";
import { getPosPickState } from "../../runtime/posPickState";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  buildSpatialTendonWrapGeomRefSet,
  getInertialSelectionKeyFromUserData,
  safeTraverse,
} from "../scene/sceneObjectLookups";
import { EMPTY_OBJECT, devLog } from "../scene/sceneConstants";
import {
  isTendonConstraintMarkerKey,
  markerLabelsVisibleForKey,
  markerLayerVisibleForKey,
} from "../scene/constraintMarkerVisibility";
import { setTendonWrapGeomObjectHighlight } from "../scene/tendonStyle";
import { getManagedLabelKind } from "../labelUtils";
import { stripNamespacePrefix } from "../../utils/nameUtils";

export function useSceneVisibilityRules({
  allTendons,
  bodyVisibility,
  bodyVisibilityUi,
  cameraMarkerVisibility,
  collisionGeomVisibility,
  constraintMarkerLayerVisible,
  constraintVisibilityUi,
  effectiveWorldBodies,
  equalityMarkerVisibility,
  focusBodyUuid,
  focusCollisionGeomUuid,
  focusMeshUuid,
  focusModeActive,
  getJointIdFromObject,
  highlightedJoint,
  inertialVisibility,
  inertialVisibilityUi,
  jointVisibilityUi,
  jointVizVisibility,
  lastSelectionSource,
  meshVisibility,
  pendingMeshLoadBodyCountsRef,
  pendingMeshLoadBodyVersion,
  safeBodies,
  sceneObjects,
  sceneRef,
  selectedBodiesMultiKey,
  selectedBody,
  selectedEquality,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedSite,
  selectedTendon,
  showCollision,
  showActuators,
  showConstraintMarkers,
  showInertials,
  showJoints,
  showJointPositionSlider,
  showSavedCameraMarkers,
  showSites,
  showVisual,
  showViewportBodies,
  siteVisibility,
  siteVisibilityUi,
  tendonMarkerLayerVisible,
  tendonMarkerVisibility,
}) {
  // Update mesh, collision, site, and joint visibility when toggles change
  useEffect(() => {
    if (!sceneRef.current) return;

    // Only run the effect if there are actual scene objects to process
    const sceneObjectKeys = Object.keys(sceneObjects);
    const hasModelGroup = !!sceneRef.current.getObjectByName("modelGroup");
    if (sceneObjectKeys.length === 0 && !hasModelGroup) {
      devLog(`[Scene] Visibility effect skipped - no scene objects to process`);
      return;
    }

    const verboseVisibilityLog =
      typeof window !== "undefined" && window.__sceneVerboseVisibilityLog === true;
    if (verboseVisibilityLog) {
      devLog(
        `[Scene] Visibility effect triggered. showVisual: ${showVisual}, showCollision: ${showCollision}, showSites: ${showSites}, showJoints: ${showJoints}`
      );
      devLog(`[Scene] Current sceneObjects keys:`, sceneObjectKeys);
      devLog(`[Scene] sceneObjects count:`, sceneObjectKeys.length);
      devLog(
        `[Scene] sceneObjects details:`,
        Object.entries(sceneObjects).map(([key, obj]) => ({
          key,
          type: obj.type,
          children: obj.children?.length || 0,
          visible: obj.visible,
          userData: obj.userData,
        }))
      );
    }

    const isFocusOnSelectedJoint =
      !!jointVisibilityUi.focus && typeof selectedJoint === "string" && !!selectedJoint;
    const isFocusOnSelectedSite =
      !!siteVisibilityUi.focus && typeof selectedSite === "string" && !!selectedSite;
    const isFocusOnSelectedInertial =
      !!inertialVisibilityUi.focus && typeof selectedInertial === "string" && !!selectedInertial;
    const sameId = (a, b) => a != null && b != null && String(a) === String(b);
    const selectionBodiesForFocus = useSelectionStore.getState().selectedBodies;
    const selectionFocusBodiesMulti =
      focusModeActive && selectionBodiesForFocus.length > 1 ? selectionBodiesForFocus : null;
    const markerKeyMatchesFocus = (markerKey) => {
      if (markerKey == null) return false;
      const keyStr = String(markerKey);
      if (keyStr.startsWith("tendon:")) {
        return (
          typeof selectedTendon === "string" &&
          sameId(keyStr.slice("tendon:".length), selectedTendon)
        );
      }
      return typeof selectedEquality === "string" && sameId(keyStr, selectedEquality);
    };
    const isFocusOnSelectedConstraintOrTendon =
      !!constraintVisibilityUi.focus &&
      ((typeof selectedEquality === "string" && !!selectedEquality) ||
        (typeof selectedTendon === "string" && !!selectedTendon));
    const activeTendonWrapGeomRefs = buildSpatialTendonWrapGeomRefSet(allTendons, {
      tendonMarkerLayerVisible,
      selectedTendon,
      tendonMarkerVisibility,
      focusSelectedOnly:
        !!constraintVisibilityUi.focus && typeof selectedTendon === "string" && !!selectedTendon,
    });
    const isActiveTendonWrapGeom = (obj) => {
      if (!obj?.userData || activeTendonWrapGeomRefs.size === 0) return false;
      const geomUuid = obj.userData.geomUuid;
      const geomName = obj.userData.geomName;
      return (
        (geomUuid != null && activeTendonWrapGeomRefs.has(String(geomUuid))) ||
        (geomName != null && activeTendonWrapGeomRefs.has(String(geomName)))
      );
    };
    const isRenderableSceneThing = (node) => {
      if (!node || !node.userData) return false;
      if (node.userData.isBodyLabelMarker) return false;
      if (node.userData.isHighlightOverlay || node.userData.isGlow) return false;
      if (node.userData.isJointCenterPickProxy) return false;
      if (getManagedLabelKind(node)) return false;
      if (node.name === "sceneFloor" || node.name === "sceneGrid") return false;
      if (node.userData.type === "site" && node.userData.bodyUuid != null) return true;
      const isRenderable =
        node.isMesh ||
        node.isLine ||
        node.isLineSegments ||
        node.isSprite ||
        node.isLine2 ||
        node.type === "Line2";
      if (!isRenderable) return false;
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      if (materials.length > 0 && materials.every((material) => material?.opacity === 0)) {
        return false;
      }
      return true;
    };
    const isVisibleThroughAncestors = (node) => {
      let p = node;
      while (p) {
        if (p.visible === false) return false;
        p = p.parent;
      }
      return true;
    };
    const getAncestorUserDataValue = (node, key) => {
      let p = node;
      while (p) {
        if (p.userData && p.userData[key] != null) return p.userData[key];
        p = p.parent;
      }
      return null;
    };
    const getBodyUuidForSceneObject = (node) =>
      node?.userData?.bodyUuid ?? getAncestorUserDataValue(node, "bodyUuid");
    const getAncestorJointId = (node) => getAncestorUserDataValue(node, "jointUuid");
    const getManagedLabelOwnerBodyUuid = (node) => getBodyUuidForSceneObject(node);
    const isSelectedJointOwner = (owner) => sameId(owner, selectedJoint);
    const shouldShowSelectedJoint = (owner) => {
      if (!isSelectedJointOwner(owner)) return false;
      const visibilityKey =
        owner != null &&
        jointVizVisibility &&
        Object.prototype.hasOwnProperty.call(jointVizVisibility, owner)
          ? owner
          : null;
      return visibilityKey == null || jointVizVisibility[visibilityKey] !== false;
    };
    const isJointMarkerGroupVisible = (obj) => {
      if (!obj?.userData?.isJointVizGroup) return false;
      const owner = obj.userData.jointUuid;
      const hasExplicitVisibility =
        owner != null &&
        jointVizVisibility &&
        Object.prototype.hasOwnProperty.call(jointVizVisibility, owner);
      const baseVisible = hasExplicitVisibility ? !!jointVizVisibility[owner] : !!showJoints;
      const selectedOverride = !hasExplicitVisibility && owner != null && selectedJoint === owner;
      const visible = baseVisible || selectedOverride;
      return isFocusOnSelectedJoint ? visible && sameId(owner, selectedJoint) : visible;
    };
    const hasVisibleJointMarker = () => {
      if (showJoints) return true;
      let visible = false;
      safeTraverse(sceneRef.current, (obj) => {
        if (!visible && isJointMarkerGroupVisible(obj)) visible = true;
      });
      return visible;
    };
    const makeVisualTransparent = showSites || showCollision || hasVisibleJointMarker();
    const shouldShowSelectedSite = (owner) =>
      sameId(owner, selectedSite) && (owner == null || (siteVisibility[owner] ?? true));
    const shouldShowSelectedGeom = (geomKey) =>
      sameId(geomKey, selectedGeom) &&
      (geomKey == null || (collisionGeomVisibility[geomKey] ?? true));
    const shouldShowSelectedInertial = (inertialKey) =>
      sameId(inertialKey, selectedInertial) &&
      (inertialKey == null || (inertialVisibility[inertialKey] ?? true));
    const isHiddenBySelectionFocus = (node) => {
      if (!focusModeActive) return false;
      if (focusMeshUuid || focusCollisionGeomUuid) return true;
      const ownerBodyUuid = getManagedLabelOwnerBodyUuid(node);
      if (selectionFocusBodiesMulti) {
        if (!ownerBodyUuid) return true;
        return !selectionFocusBodiesMulti.has(String(ownerBodyUuid));
      }
      if (!focusBodyUuid) return true;
      return !sameId(ownerBodyUuid, focusBodyUuid);
    };
    const getManagedLabelVisibility = (node, kind) => {
      if (isHiddenBySelectionFocus(node)) return false;

      if (kind === "site") {
        const owner = node.userData?.siteUuid ?? getAncestorUserDataValue(node, "siteUuid");
        if (isFocusOnSelectedSite) {
          return (
            (showSites || shouldShowSelectedSite(owner)) &&
            !siteVisibilityUi.labelsOff &&
            sameId(owner, selectedSite)
          );
        }
        return (showSites || shouldShowSelectedSite(owner)) && !siteVisibilityUi.labelsOff;
      }

      if (kind === "joint") {
        const owner = getAncestorJointId(node);
        if (isFocusOnSelectedJoint) {
          return (
            (showJoints || shouldShowSelectedJoint(owner)) &&
            !jointVisibilityUi.labelsOff &&
            sameId(owner, selectedJoint)
          );
        }
        return (showJoints || shouldShowSelectedJoint(owner)) && !jointVisibilityUi.labelsOff;
      }

      if (kind === "constraint") {
        const key = node.userData?.constraintMarkerKey;
        if (!markerLabelsVisibleForKey(key, constraintVisibilityUi)) return false;
        const keyStr = key != null ? String(key) : "";
        if (keyStr.startsWith("tendon:")) {
          const tendonUuid = keyStr.slice("tendon:".length);
          if ((tendonMarkerVisibility[tendonUuid] ?? true) === false) {
            return false;
          }
        } else if (keyStr && (equalityMarkerVisibility[keyStr] ?? true) === false) {
          return false;
        }
        const markerLayerVisible = markerLayerVisibleForKey(key, constraintVisibilityUi)
          ? isTendonConstraintMarkerKey(key)
            ? tendonMarkerLayerVisible
            : constraintMarkerLayerVisible
          : false;
        if (isFocusOnSelectedConstraintOrTendon) {
          return (markerLayerVisible || markerKeyMatchesFocus(key)) && markerKeyMatchesFocus(key);
        }
        return markerLayerVisible || markerKeyMatchesFocus(key);
      }

      if (kind === "body") {
        return false;
      }

      return true;
    };

    safeTraverse(sceneRef.current, (obj) => {
      // Uploaded mesh detection removed - only XML-based robot loading is supported

      // Skip IK target marker - it manages its own visibility (picked/cleared).
      if (
        obj?.name === "__ikTargetMarker" ||
        (obj.userData && (obj.userData.isIkTargetMarker || obj.userData.type === "ikTargetMarker"))
      ) {
        return;
      }

      // Skip glow spheres - they are handled separately
      if (obj.userData && obj.userData.isGlow) {
        return;
      }

      if (obj.userData && obj.userData.isActuatorVisualization) {
        obj.visible = !!showActuators && !isHiddenBySelectionFocus(obj);
        return;
      }

      const savedCamUuid = obj.userData?.savedSceneCameraUuid;
      if (savedCamUuid != null && savedCamUuid !== "") {
        const itemVisible = cameraMarkerVisibility[String(savedCamUuid)] ?? true;
        obj.visible = showSavedCameraMarkers && itemVisible;
        return;
      }

      // Constraint / tendon marker overlays: visibility follows global marker toggle + inspector focus.
      if (obj.userData && obj.userData.isConstraintVisualization) {
        const key = obj.userData.key;
        const selectedConstraintMarker = key != null && markerKeyMatchesFocus(key);
        const markerLayerVisible = markerLayerVisibleForKey(key, constraintVisibilityUi)
          ? isTendonConstraintMarkerKey(key)
            ? tendonMarkerLayerVisible
            : constraintMarkerLayerVisible
          : false;
        if (key != null) {
          const keyStr = String(key);
          if (keyStr.startsWith("tendon:")) {
            const tendonUuid = keyStr.slice("tendon:".length);
            if ((tendonMarkerVisibility[tendonUuid] ?? true) === false) {
              obj.visible = false;
              return;
            }
          } else if ((equalityMarkerVisibility[keyStr] ?? true) === false) {
            obj.visible = false;
            return;
          }
        }
        if (!markerLayerVisible && !selectedConstraintMarker) {
          obj.visible = false;
          return;
        }
        if (isFocusOnSelectedConstraintOrTendon && key != null && !markerKeyMatchesFocus(key)) {
          obj.visible = false;
          return;
        }
        obj.visible = true;
        return;
      }

      if (obj.userData && obj.userData.isSlideJointRangeVisual) {
        return;
      }

      // Joint endpoint spheres (red min/max markers)
      if (obj.userData && obj.userData.isJointEndpoint) {
        const owner = getAncestorJointId(obj);
        if (isFocusOnSelectedJoint) {
          obj.visible = (showJoints || shouldShowSelectedJoint(owner)) && owner === selectedJoint;
        } else {
          obj.visible = showJoints || shouldShowSelectedJoint(owner);
        }
        return;
      }

      // Joint marker spheres (center/green): always show with showJoints
      if (obj.userData && getJointIdFromObject(obj)) {
        const owner = getAncestorJointId(obj);
        if (isFocusOnSelectedJoint) {
          obj.visible = (showJoints || shouldShowSelectedJoint(owner)) && owner === selectedJoint;
        } else {
          obj.visible = showJoints || shouldShowSelectedJoint(owner);
        }
        return;
      }

      // Inertia ellipsoids: only show with showInertials
      if (obj.userData && (obj.userData.isInertial || obj.userData.type === "inertial")) {
        const inertialKey = getInertialSelectionKeyFromUserData(obj.userData);
        const inertialItemVisible =
          inertialKey == null || (inertialVisibility[inertialKey] ?? true);
        if (isFocusOnSelectedInertial) {
          obj.visible =
            ((showInertials && inertialItemVisible) || shouldShowSelectedInertial(inertialKey)) &&
            inertialKey != null &&
            inertialKey === selectedInertial;
        } else {
          obj.visible =
            (showInertials && inertialItemVisible) || shouldShowSelectedInertial(inertialKey);
        }
        return;
      }

      // Update collision visibility with per-geom override
      if (obj.userData && obj.userData.type === "collision") {
        const geomBodyUuid = obj.userData.bodyUuid || null;
        const geomUuid = obj.userData.geomUuid || null;
        const geomKey = geomBodyUuid && geomUuid ? `${geomBodyUuid}:${geomUuid}` : null;
        const geomItemVisible = geomKey == null || (collisionGeomVisibility[geomKey] ?? true);
        const highlightTendonWrap = isActiveTendonWrapGeom(obj) && geomItemVisible;
        const visible =
          (showCollision && geomItemVisible) ||
          shouldShowSelectedGeom(geomKey) ||
          highlightTendonWrap;
        updateCollisionVisibility(obj, visible);
        setTendonWrapGeomObjectHighlight(obj, highlightTendonWrap && visible);
        return;
      }
      updateCollisionVisibility(obj, showCollision);

      // Sites (Group/Mesh in `processBodyFromStore`). CSS2D site labels also use
      // userData.type === "site" but must follow managed-label visibility (labelsOff, etc.).
      if (obj.userData && obj.userData.type === "site" && getManagedLabelKind(obj) !== "site") {
        const owner = obj.userData.siteUuid;
        const siteItemVisible = !owner || (siteVisibility[owner] ?? true);
        if (isFocusOnSelectedSite) {
          obj.visible =
            ((showSites && siteItemVisible) || shouldShowSelectedSite(owner)) &&
            owner === selectedSite;
        } else {
          obj.visible = (showSites && siteItemVisible) || shouldShowSelectedSite(owner);
        }
        return;
      }

      // Visual primitive geoms (Group created in `processBodyFromStore`)
      if (obj.userData && obj.userData.type === "visual") {
        const visBodyUuid = obj.userData.bodyUuid || null;
        const visGeomUuid = obj.userData.geomUuid || null;
        const geomKey = visBodyUuid && visGeomUuid ? `${visBodyUuid}:${visGeomUuid}` : null;
        const geomItemVisible = geomKey == null || (collisionGeomVisibility[geomKey] ?? true);
        const bodyItemVisible = !visBodyUuid || (bodyVisibility[visBodyUuid] ?? true);
        const highlightTendonWrap =
          isActiveTendonWrapGeom(obj) && bodyItemVisible && geomItemVisible;
        obj.visible =
          (showVisual && bodyItemVisible && geomItemVisible) ||
          (bodyItemVisible && shouldShowSelectedGeom(geomKey)) ||
          highlightTendonWrap;
        setTendonWrapGeomObjectHighlight(obj, highlightTendonWrap && obj.visible);
        return;
      }

      // Visual meshes (Mesh children tagged in `processBodyFromStore`)
      if (
        obj.isMesh &&
        obj.material &&
        obj.name !== "sceneFloor" &&
        obj.name !== "sceneGrid" &&
        obj.userData &&
        obj.userData.type === "mesh"
      ) {
        const meshBodyUuid = obj.userData.bodyUuid || null;
        const meshGeomUuid = obj.userData.geomUuid || null;
        const meshGeomKey = meshBodyUuid && meshGeomUuid ? `${meshBodyUuid}:${meshGeomUuid}` : null;
        const meshName = obj.userData.meshName || null;
        const bodyItemVisible = !meshBodyUuid || (bodyVisibility[meshBodyUuid] ?? true);
        const meshItemVisible = !meshName || (meshVisibility[meshName] ?? true);
        const geomItemVisible =
          meshGeomKey == null || (collisionGeomVisibility[meshGeomKey] ?? true);
        const highlightTendonWrap =
          isActiveTendonWrapGeom(obj) && bodyItemVisible && geomItemVisible;
        obj.visible =
          (showVisual && bodyItemVisible && meshItemVisible) ||
          (bodyItemVisible && meshItemVisible && shouldShowSelectedGeom(meshGeomKey)) ||
          highlightTendonWrap;

        const applyOverlayToMaterial = (m) => {
          if (!m || m.userData?.isCollisionGeomMaterial) return;
          if (makeVisualTransparent) {
            m.transparent = true;
            m.opacity = 0.6;
          } else {
            const u = m.userData || {};
            m.opacity = u.critterBaseOpacity != null ? u.critterBaseOpacity : 1.0;
            m.transparent = u.critterBaseTransparent != null ? u.critterBaseTransparent : false;
            m.depthWrite = u.critterBaseDepthWrite != null ? u.critterBaseDepthWrite : true;
            if (u.critterBaseAlphaTest != null) m.alphaTest = u.critterBaseAlphaTest;
            if (u.critterBasePolygonOffset != null) m.polygonOffset = u.critterBasePolygonOffset;
            if (u.critterBasePolygonOffsetFactor != null) {
              m.polygonOffsetFactor = u.critterBasePolygonOffsetFactor;
            }
            if (u.critterBasePolygonOffsetUnits != null) {
              m.polygonOffsetUnits = u.critterBasePolygonOffsetUnits;
            }
          }
          m.needsUpdate = true;
        };

        // Make visual meshes slightly transparent if any overlays are on (restore MJCF-driven
        // transparency/depth when overlays are off so texture-alpha decals keep working).
        if (Array.isArray(obj.material)) {
          obj.material.forEach(applyOverlayToMaterial);
        } else {
          applyOverlayToMaterial(obj.material);
        }
        setTendonWrapGeomObjectHighlight(obj, highlightTendonWrap && obj.visible);
      }
      // Joint arcs (lines)
      if (obj.userData && obj.userData.isJointArc) {
        if (obj.userData.isSlideJointRangeVisual) return;
        const owner = getAncestorJointId(obj);
        if (isFocusOnSelectedJoint) {
          obj.visible = (showJoints || shouldShowSelectedJoint(owner)) && owner === selectedJoint;
        } else {
          obj.visible = showJoints || shouldShowSelectedJoint(owner);
        }
      }
      const managedLabelKind = getManagedLabelKind(obj);
      if (managedLabelKind) {
        obj.visible = getManagedLabelVisibility(obj, managedLabelKind);
      }
    });

    const bodyUuidsWithVisibleObjects = new Set();
    safeTraverse(sceneRef.current, (obj) => {
      if (!isRenderableSceneThing(obj) || !isVisibleThroughAncestors(obj)) return;
      const ownerBodyUuid = getBodyUuidForSceneObject(obj);
      if (ownerBodyUuid == null || ownerBodyUuid === "") return;
      bodyUuidsWithVisibleObjects.add(String(ownerBodyUuid));
    });

    const rootBodyUuids = new Set();
    const objectLabelByRootBodyUuid = new Map();
    (effectiveWorldBodies || []).forEach((worldBody) => {
      const rootUuid =
        typeof worldBody === "string" ? worldBody : worldBody?.uuid || worldBody?.rootBodyUuid;
      if (!rootUuid) return;
      const rootKey = String(rootUuid);
      rootBodyUuids.add(rootKey);
      const objectLabel =
        typeof worldBody === "object" && typeof worldBody.name === "string" && worldBody.name.trim()
          ? worldBody.name.trim()
          : safeBodies?.[rootKey]?.name || rootKey;
      objectLabelByRootBodyUuid.set(rootKey, objectLabel);
    });
    if (rootBodyUuids.size === 0) {
      const childBodyUuids = new Set();
      Object.values(safeBodies || EMPTY_OBJECT).forEach((body) => {
        (body?.children || []).forEach((childUuid) => {
          if (childUuid) childBodyUuids.add(String(childUuid));
        });
      });
      Object.keys(safeBodies || EMPTY_OBJECT).forEach((bodyUuid) => {
        if (!childBodyUuids.has(bodyUuid)) rootBodyUuids.add(bodyUuid);
      });
    }

    const parentByBodyUuid = new Map();
    Object.entries(safeBodies || EMPTY_OBJECT).forEach(([parentUuid, body]) => {
      const childRefs = Array.isArray(body?.children) ? body.children : [];
      childRefs.forEach((childRef) => {
        const childUuid =
          typeof childRef === "string" ? childRef : childRef?.uuid || childRef?.bodyUuid;
        if (childUuid) {
          parentByBodyUuid.set(String(childUuid), String(parentUuid));
        }
      });
    });
    const getRootBodyUuid = (bodyUuid) => {
      let current = bodyUuid;
      const visited = new Set();
      while (current && parentByBodyUuid.has(current) && !visited.has(current)) {
        visited.add(current);
        current = parentByBodyUuid.get(current) || current;
      }
      return current || bodyUuid;
    };
    const rootBodyUuidsWithVisibleObjects = new Set();
    bodyUuidsWithVisibleObjects.forEach((bodyUuid) => {
      const rootUuid = getRootBodyUuid(String(bodyUuid));
      if (rootUuid) rootBodyUuidsWithVisibleObjects.add(rootUuid);
    });

    const bodyUuidsNeedingLabelMarker = new Set();
    safeTraverse(sceneRef.current, (obj) => {
      if (!obj.userData?.isBodyLabelMarker) return;
      const bodyUuid = obj.userData.bodyUuid;
      const bodyKey = bodyUuid != null ? String(bodyUuid) : "";
      const bodyItemVisible = bodyKey === "" || (bodyVisibility[bodyKey] ?? true);
      const hasPendingMeshLoad =
        bodyKey !== "" && pendingMeshLoadBodyCountsRef.current.has(bodyKey);
      const rootBodyUuid = bodyKey !== "" ? getRootBodyUuid(bodyKey) : "";
      const treeContainsVisibleObjects =
        rootBodyUuid !== "" && rootBodyUuidsWithVisibleObjects.has(rootBodyUuid);
      const isObjectSelectionSource =
        lastSelectionSource === "sceneTreeObject" ||
        lastSelectionSource === "objectLibrary" ||
        lastSelectionSource === "objectPropertiesLink";
      const isSelectedBodyMarker =
        bodyKey !== "" && sameId(bodyKey, selectedBody) && !isObjectSelectionSource;
      const visible =
        showViewportBodies &&
        bodyVisibilityUi.bodyLabelMarkers &&
        bodyItemVisible &&
        !hasPendingMeshLoad &&
        !isHiddenBySelectionFocus(obj) &&
        bodyKey !== "" &&
        ((rootBodyUuids.has(bodyKey) &&
          !bodyUuidsWithVisibleObjects.has(bodyKey) &&
          !treeContainsVisibleObjects) ||
          isSelectedBodyMarker);
      obj.visible = visible;
      if (visible) bodyUuidsNeedingLabelMarker.add(bodyKey);
    });

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj.userData?.isBodyLabel) return;
      const bodyUuid = obj.userData.bodyUuid;
      const bodyKey = bodyUuid != null ? String(bodyUuid) : "";
      const bodyItemVisible = bodyKey === "" || (bodyVisibility[bodyKey] ?? true);
      const objectLabel = objectLabelByRootBodyUuid.get(bodyKey);
      const showsObjectLabel = bodyVisibilityUi.objectLabels && objectLabel != null;
      obj.visible =
        showViewportBodies &&
        bodyItemVisible &&
        !isHiddenBySelectionFocus(obj) &&
        bodyKey !== "" &&
        (bodyVisibilityUi.labels || showsObjectLabel || bodyUuidsNeedingLabelMarker.has(bodyKey));
      if (obj.element) {
        const rawBodyLabel = obj.userData.bodyName || safeBodies?.[bodyKey]?.name || "body";
        const bodyLabel = stripNamespacePrefix(rawBodyLabel) || "body";
        const nextText = showsObjectLabel ? objectLabel : bodyLabel;
        if (obj.element.textContent !== nextText) obj.element.textContent = nextText;
      }
    });

    // Ensure position pick marker is hidden unless actively in pick mode
    // (it will be shown/hidden by the hover handler when pick mode is active)
    if (sceneRef.current) {
      const marker = sceneRef.current.getObjectByName("__posPickMarker");
      if (marker && !getPosPickState().active) {
        marker.visible = false;
      }
    }
  }, [
    showVisual,
    showActuators,
    showCollision,
    showSites,
    showJoints,
    showInertials,
    highlightedJoint,
    selectedJoint,
    selectedGeom,
    sceneObjects,
    jointVisibilityUi.focus,
    jointVisibilityUi.labelsOff,
    showJointPositionSlider,
    showViewportBodies,
    bodyVisibilityUi.labels,
    bodyVisibilityUi.objectLabels,
    bodyVisibilityUi.bodyLabelMarkers,
    effectiveWorldBodies,
    safeBodies,
    selectedSite,
    siteVisibilityUi.focus,
    siteVisibilityUi.labelsOff,
    selectedInertial,
    inertialVisibilityUi.focus,
    constraintVisibilityUi.focus,
    constraintVisibilityUi.labelsOff,
    constraintVisibilityUi.tendonLabelsOff,
    constraintVisibilityUi.constraintsVisible,
    constraintVisibilityUi.tendonsVisible,
    selectedEquality,
    selectedTendon,
    allTendons,
    showConstraintMarkers,
    tendonMarkerLayerVisible,
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    bodyVisibility,
    pendingMeshLoadBodyVersion,
    meshVisibility,
    siteVisibility,
    jointVizVisibility,
    collisionGeomVisibility,
    inertialVisibility,
    equalityMarkerVisibility,
    tendonMarkerVisibility,
    cameraMarkerVisibility,
    showSavedCameraMarkers,
    selectedBody,
    lastSelectionSource,
    selectedBodiesMultiKey,
    constraintMarkerLayerVisible,
    constraintVisibilityUi,
    getJointIdFromObject,
    pendingMeshLoadBodyCountsRef,
    sceneRef,
  ]);
}
